export type SectionState = {
  sectionKey: string
  html: string
  overrides: Record<string, string>
  /** Section order before a whole-section removal; absent for in-section removals. */
  order?: string[]
}

export function collectSubtreeEditKeys(subtreeHtml: string): string[] {
  const container = document.createElement('div')
  container.innerHTML = subtreeHtml
  const keys: string[] = []
  for (const el of Array.from(container.querySelectorAll('[data-edit-key]'))) {
    const key = el.getAttribute('data-edit-key')
    if (key && !keys.includes(key)) keys.push(key)
  }
  return keys
}

export function pruneTextOverrides(
  overrides: Record<string, string>,
  keys: string[]
): Record<string, string> {
  const doomed = new Set(keys)
  const next: Record<string, string> = {}
  for (const [key, value] of Object.entries(overrides)) {
    if (doomed.has(key)) continue
    next[key] = value
  }
  return next
}

export function captureSectionState(state: SectionState): SectionState {
  return {
    sectionKey: state.sectionKey,
    html: state.html,
    overrides: { ...state.overrides },
    ...(state.order ? { order: [...state.order] } : {})
  }
}

export function restoreSectionState(snapshot: SectionState): SectionState {
  return captureSectionState(snapshot)
}

/**
 * Batch undo entries (FE-6248) hold one pre-op snapshot per affected section.
 * Same-section removals chain during the batch, so only the FIRST snapshot is
 * the pre-batch state — restoring it alone rewinds every removal in that
 * section. Keep-first dedupe preserves that snapshot and drops the rest.
 */
export function dedupeUndoSnapshots(snapshots: SectionState[]): SectionState[] {
  const seen = new Set<string>()
  const kept: SectionState[] = []
  for (const snapshot of snapshots) {
    if (seen.has(snapshot.sectionKey)) continue
    seen.add(snapshot.sectionKey)
    kept.push(snapshot)
  }
  return kept
}

/**
 * Restores a whole batch entry as one action: every affected section reverts
 * to its pre-batch html, and the overrides map reverts to the pre-batch map —
 * the first snapshot's, since pruning only ever removes keys.
 */
export function restoreBatchSnapshots(
  entry: SectionState[],
  sections: Record<string, string>,
  currentOverrides?: Record<string, string>
): {
  sections: Record<string, string>
  overrides: Record<string, string>
  order: string[] | null
} | null {
  const base = entry[0]
  if (!base) return null
  const restored = { ...sections }
  for (const snapshot of entry) {
    if (snapshot.sectionKey) restored[snapshot.sectionKey] = snapshot.html
  }
  const order = entry.find(snapshot => snapshot.order)?.order
  return {
    sections: restored,
    overrides: currentOverrides
      ? mergeRestoredOverrides(base.overrides, currentOverrides)
      : { ...base.overrides },
    order: order ? [...order] : null
  }
}

/**
 * Undo reverts only what the removal itself changed: pruned keys come back and
 * a blanked chrome key is cleared, while text edited after the removal stays.
 */
function mergeRestoredOverrides(
  base: Record<string, string>,
  current: Record<string, string>
): Record<string, string> {
  const merged = { ...current }
  for (const [key, value] of Object.entries(base)) {
    if (!(key in current)) merged[key] = value
  }
  for (const [key, value] of Object.entries(current)) {
    if (value === '' && !(key in base)) delete merged[key]
  }
  return merged
}

export function dropOrphanedScope<T extends { key?: string; sectionKey: string }>(
  target: T | null,
  sections: Record<string, string>
): T | null {
  if (!target) return null
  if (target.key) {
    const inDocument = Object.values(sections).some(html =>
      collectSubtreeEditKeys(html).includes(target.key as string)
    )
    return inDocument ? target : null
  }
  return target.sectionKey in sections ? target : null
}
